import queryString from 'query-string';
import { pathOr } from 'ramda';
import { delay } from 'redux-saga';
import {
  all,
  call,
  put,
  race,
  select,
  spawn,
  take,
  takeLatest,
} from 'redux-saga/effects';

import { Button, theme, createStandaloneToast } from '@frontend/components';

import { PART_STATUSES, PAGE_SIZE, PROCESSING_CATEGORIES } from 'constants/all';

import { fileUploadSaga } from 'modules/common/modals/uploadDetailsModal/data/sagas';
import { partViewerRoutePrefix } from 'modules/viewerPage/data/routes';

import partLibraryService from 'services/partLibraryService';
import partService from 'services/partService';
import quoteService from 'services/quoteService';

import { getNextLocation, getPrevLocation } from 'store/app/selectors';
import { PAGE_REQUEST } from 'store/app/types';
import { getGroupId } from 'store/session/selectors';

import history from 'utils/browserHistory';
import { formatRevisedQuoteNumber } from 'utils/formatters';
import { isStatusInProgress } from 'utils/partUtils';
import { buildLineItemsFileInfo } from 'utils/partUtils';
import { paramToSearchString, searchStringToParam } from 'utils/stringUtils';

import {
  archivedPartsList,
  archiveParts,
  copyPartsToQuote,
  draftedQuotes,
  getAdditionalQuotes,
  mergeParts,
  partLibraryPage,
  partsList,
  purgeParts,
  receivePart,
  receivePartStatuses,
  restoreParts,
  sharesList,
  types,
  uploadPartFile,
  uploadPartFiles,
  uploadSupportingFilesError,
} from './actions';
import { TAB_TYPES } from './constants';
import {
  getActiveTab,
  getArchivedPartsCount,
  getPage,
  getPartStatuses,
  getQuotesPage,
  getSearchString,
  getSelectedPartsMap,
  getSelectedPartsUuidArray,
  getSelectedQuote,
  getTotalPartCount,
  isCreatingQuote,
} from './reducer';

const { toast } = createStandaloneToast({ theme });

export default function* root() {
  yield takeLatest(types.PART_LIBRARY_CHANGE_TAB, watchChangeTab);
  yield takeLatest(
    types.PART_LIBRARY_PAGE_ARCHIVE_PARTS_REQUEST,
    watchArchiveParts,
  );
  yield takeLatest(
    types.PART_LIBRARY_PAGE_RESTORE_PARTS_REQUEST,
    watchRestoreParts,
  );
  yield takeLatest(
    types.PART_LIBRARY_PAGE_PURGE_PARTS_REQUEST,
    watchPurgeParts,
  );
  yield takeLatest(types.PART_LIBRARY_PAGE_PAGINATE, watchPaginate);
  yield takeLatest(types.PART_LIBRARY_PAGE_SEARCH, watchSearch);
  yield takeLatest(
    types.PART_LIBRARY_PAGE_ADD_SELECTED_PART,
    watchAddSelectedPart,
  );
  yield takeLatest(
    types.PART_LIBRARY_PAGE_REMOVE_SELECTED_PART,
    watchRemoveSelectedPart,
  );
  yield takeLatest(types.PART_LIBRARY_PAGE_OPEN_PART, watchOpenPart);
  yield takeLatest(
    types.PART_LIBRARY_PAGE_OPEN_ADD_TO_QUOTE_DRAWER,
    watchOpenAddToQuoteDrawer,
  );
  yield takeLatest(
    types.PART_LIBRARY_PAGE_OPEN_NESTED_ADD_TO_QUOTE_DRAWER,
    watchOpenAddToQuoteDrawer,
  );
  yield takeLatest(types.PARTS_LIST_REQUEST, watchPartsList);
  yield takeLatest(types.SHARES_LIST_REQUEST, watchSharesList);
  yield takeLatest(types.ARCHIVED_PARTS_LIST_REQUEST, watchArchivedPartsList);
  yield takeLatest(types.UPLOAD_PART_FILES_REQUEST, watchPartUploads);
  yield takeLatest(types.DRAFTED_QUOTES_REQUEST, watchDraftedQuotesRequest);
  yield takeLatest(types.COPY_PARTS_TO_QUOTE_REQUEST, watchCopyPartsToQuote);
  yield takeLatest(types.PART_LIBRARY_PAGE_INIT, watchInit);
  yield takeLatest(
    types.GET_ADDITIONAL_QUOTES_REQUEST,
    watchGetAdditionalQuotes,
  );
  yield takeLatest(types.MERGE_PARTS_REQUEST, watchMergePartsAsSupportingFiles);
  yield spawn(pageWorker);
}

function* watchInit() {
  try {
    const tabParam = queryString.parse(window.location.search).tab;
    const tab = tabParam
      ? TAB_TYPES[tabParam.toUpperCase()] || TAB_TYPES.OUR_PARTS
      : TAB_TYPES.OUR_PARTS;
    const page = parseInt(queryString.parse(window.location.search).page || 1);
    const searchString = queryString.parse(window.location.search).search
      ? paramToSearchString(queryString.parse(window.location.search).search)
      : null;
    const prevLocation = yield select(getPrevLocation);
    if (!prevLocation || prevLocation.pathname != '/parts') {
      yield put(partLibraryPage.closeActionBar());
    }
    yield put(partLibraryPage.setPage({ pageNumber: page }));
    yield put(partLibraryPage.setTab({ tab }));
    yield put(partLibraryPage.setSearch({ searchString }));
    yield put(partsList.request());
    yield put(sharesList.request());
    yield put(archivedPartsList.request());
  } catch (error) {
    console.error('Encountered an error initializing the part library', error);
  }
}

function* pageWorker() {
  // restart "checkPartStatuses" saga when
  // PAGE_REQUEST is dispatched
  for (;;) {
    yield race({
      partList: call(periodicallyRefreshPartList),
      partStatuses: call(periodicallyCheckPartStatuses),
      otherPage: take(PAGE_REQUEST),
    });
  }
}

function* watchChangeTab(action) {
  //This is a hack to prevent someone from changing tabs on the view while the app is trying to navigate to the viewer
  //which would cause a crash
  const nextLocation = yield select(getNextLocation);
  if (!nextLocation.pathname.includes('viewer')) {
    const { tab } = action.payload;
    const searchString = yield select(getSearchString);
    history.push(
      `parts?page=1&tab=${tab}${
        searchString ? `&search=${searchStringToParam(searchString)}` : ''
      }`,
    );
  }
}

function* watchPartUploads(action) {
  const { payload } = action;
  const { file } = payload;

  try {
    const fileArray = Array.isArray(file) ? file : [file];
    const fileInfoList = yield call(fileUploadSaga, fileArray);

    const partFilesUploading = fileInfoList.map((fileInfo) => {
      const fileData = fileInfo.file ?? {};
      fileData.tempId = fileInfo.tempId;
      return fileData;
    });
    yield put(uploadPartFiles.setFiles({ partFilesUploading }));

    const lineItemsFileInfo = buildLineItemsFileInfo({
      fileInfos: fileInfoList,
    });
    for (const lineItemFileInfo of lineItemsFileInfo.values()) {
      yield call(watchUploadPartFile, { lineItemFileInfo });
    }
    yield put(uploadPartFiles.success());
  } catch (error) {
    yield put(uploadPartFiles.error(error));
  }
}

function* watchUploadPartFile({
  lineItemFileInfo: { primaryFileInfo, supportingFileInfos },
}) {
  try {
    const formData = new FormData();
    formData.append('original', primaryFileInfo.file);
    formData.append('units', primaryFileInfo.units);
    if (primaryFileInfo.thickness) {
      formData.append('thickness', primaryFileInfo.thickness);
    }
    const result = yield call(partLibraryService.partUpload, formData);
    const partUUID = result.uuid;
    const partMasterVersion = result.partMasterVersion;
    const payload = {
      uuid: result.uuid,
      tempId: primaryFileInfo.tempId,
      partMasterUuid: result.partMasterUuid,
      version: partMasterVersion,
      primaryFile: {
        filename: result.filename,
        status: result.status,
        uuid: result.partFileUuid,
      },
    };
    yield put(uploadPartFile.success(payload));
    const partStatuses = {
      [partUUID]: result.status,
    };
    /**
     * spawn another saga to attach supporting files so that the watchUploadPartFile saga can exit, and the user will be
     * able to view the part.
     */
    if (supportingFileInfos.length > 0) {
      yield spawn(attachSupportingFiles, {
        partMasterVersion,
        partUUID,
        supportingFileInfos,
      });
      yield call(toast, {
        title: 'Success',
        status: 'success',
        description:
          supportingFileInfos.length +
          ' file(s) automatically merged into ' +
          result.filename,
      });
    }
    yield put(receivePartStatuses({ partStatuses }));
  } catch (error) {
    console.error(error);
    throw error;
  }
}

function* attachSupportingFiles({
  partMasterVersion,
  partUUID,
  supportingFileInfos,
}) {
  try {
    const formData = new FormData();
    const uploadData = {};
    supportingFileInfos.forEach((fileInfo) => {
      formData.append(fileInfo.file.name, fileInfo.file);
      formData.append('part_master_version', partMasterVersion);
      uploadData[fileInfo.file.name] = {
        units: fileInfo.units,
        thickness: fileInfo.thickness,
      };
    });
    formData.append('upload_data', JSON.stringify(uploadData));
    yield call(partService.attachFile, partUUID, formData);
  } catch (error) {
    console.error(error);
    yield put(uploadSupportingFilesError);
  }
}

function* periodicallyRefreshPartList() {
  yield take(types.PART_LIBRARY_PAGE_INIT);
  while (true) {
    yield call(delay, 60000);
    try {
      yield all([call(watchPartsList), call(watchSharesList)]);
    } catch (error) {
      console.error(error);
    }
  }
}

function* periodicallyCheckPartStatuses() {
  yield take(types.PART_LIBRARY_PAGE_INIT);
  while (true) {
    yield call(delay, 1500);
    try {
      const oldPartStatuses = yield select(getPartStatuses);
      const pendingProcessingEntries = Object.values(oldPartStatuses).filter(
        (entry) => {
          let isPending = false;
          for (const category of Object.values(PROCESSING_CATEGORIES)) {
            if (isStatusInProgress(pathOr(null, [category, 'status'], entry))) {
              isPending = true;
              break;
            }
          }
          return isPending;
        },
      );
      if (pendingProcessingEntries.length > 0) {
        // establish mapping
        const fileUuidToPartUuid = {};
        Object.entries(oldPartStatuses).forEach(([partUuid, entry]) => {
          fileUuidToPartUuid[entry.uuid] = partUuid;
        });
        const primaryFileUuids = pendingProcessingEntries.map(
          (entry) => entry.uuid,
        );
        const response = yield call(
          partService.checkStatuses,
          primaryFileUuids,
        );
        const { processing } = response.statuses;
        const nextProcessingStatuses = {};
        for (const i in processing) {
          const partUuid = fileUuidToPartUuid[processing[i].uuid];
          nextProcessingStatuses[partUuid] = {
            ...processing[i],
          };
        }

        let areAnyDifferent = false;
        for (const [partUuid, newStatusInfo] of Object.entries(
          nextProcessingStatuses,
        )) {
          const oldStatusInfo = oldPartStatuses[partUuid];
          if (!oldStatusInfo) {
            areAnyDifferent = true;
          } else {
            let anyDifferentLocal = false;
            for (const category of Object.values(PROCESSING_CATEGORIES)) {
              // TODO: add timing considerations
              if (
                pathOr(null, [category, 'status'], newStatusInfo) !==
                pathOr(null, [category, 'status'], oldStatusInfo)
              ) {
                anyDifferentLocal = true;
              }
            }
            if (anyDifferentLocal) {
              areAnyDifferent = true;
              yield call(handleProcessingChange, {
                oldStatusInfo,
                newStatusInfo,
                partUuid,
              });
            }
          }
        }
        if (areAnyDifferent) {
          yield put(
            receivePartStatuses({ partStatuses: nextProcessingStatuses }),
          );
        }
      }
    } catch (error) {
      console.error(error);
    }
  }
}

function* handleProcessingChange({ oldStatusInfo, newStatusInfo, partUuid }) {
  const finishedViewer =
    oldStatusInfo[PROCESSING_CATEGORIES.VIEWER_CREATION].status !==
      PART_STATUSES.FINISHED &&
    newStatusInfo[PROCESSING_CATEGORIES.VIEWER_CREATION].status ===
      PART_STATUSES.FINISHED;
  const finishedThumbnail =
    oldStatusInfo[PROCESSING_CATEGORIES.THUMBNAIL_CREATION].status !==
      PART_STATUSES.FINISHED &&
    newStatusInfo[PROCESSING_CATEGORIES.THUMBNAIL_CREATION].status ===
      PART_STATUSES.FINISHED;

  const significantChange = finishedViewer || finishedThumbnail;

  if (significantChange) {
    const part = yield call(partLibraryService.get, partUuid);
    yield put(receivePart(part));
  }
}

function* watchPaginate(action) {
  const page = action.payload.pageNumber;
  const searchString = yield select(getSearchString);
  const tab = yield select(getActiveTab);
  history.push(
    `/parts?page=${page || 1}&tab=${tab}${
      searchString ? `&search=${searchStringToParam(searchString)}` : ''
    }`,
  );
}

function* watchPartsList() {
  try {
    // get query  params
    const activeTab = yield select(getActiveTab);
    // only query for first page if we are not viewing this tab
    const page = activeTab === TAB_TYPES.OUR_PARTS ? yield select(getPage) : 1;
    activeTab === TAB_TYPES.OUR_PARTS &&
      (yield put(partLibraryPage.setPage({ pageNumber: page })));
    const search = yield select(getSearchString);
    const queryParams = {
      page,
      search,
    };

    const response = yield call(partLibraryService.list, queryParams);
    const { results } = response;

    // begin polling on any parts going through geometric processing/interrogation
    const partStatuses = {};
    for (const entry of results) {
      const { primaryFile, uuid } = entry;
      const { status } = primaryFile;
      partStatuses[uuid] = status;
    }
    yield put(receivePartStatuses({ partStatuses }));

    yield put(partsList.success(response));
  } catch (error) {
    yield put(partsList.error(error));
  }
}

function* watchSearch(action) {
  const { searchString } = action.payload;
  const tab = yield select(getActiveTab);
  history.push(
    `/parts?page=1&tab=${tab}${
      searchString ? `&search=${searchStringToParam(searchString)}` : ''
    }`,
  );
}

function* watchSharesList() {
  try {
    // get query  params
    const activeTab = yield select(getActiveTab);
    // only query for first page if we are not viewing this tab
    const page =
      activeTab === TAB_TYPES.SHARED_PARTS ? yield select(getPage) : 1;
    activeTab === TAB_TYPES.SHARED_PARTS &&
      (yield put(partLibraryPage.setPage({ pageNumber: page })));
    const search = yield select(getSearchString);
    const queryParams = {
      page,
      search,
    };

    const response = yield call(
      partLibraryService.listSharedParts,
      queryParams,
    );

    yield put(sharesList.success(response));
  } catch (error) {
    yield put(sharesList.error(error));
  }
}

function* watchArchivedPartsList() {
  try {
    // get query  params
    const activeTab = yield select(getActiveTab);
    // only query for first page if we are not viewing this tab
    const page =
      activeTab === TAB_TYPES.ARCHIVED_PARTS ? yield select(getPage) : 1;
    activeTab === TAB_TYPES.ARCHIVED_PARTS &&
      (yield put(partLibraryPage.setPage({ pageNumber: page })));
    const search = yield select(getSearchString);
    const queryParams = {
      page,
      search,
    };

    const response = yield call(
      partLibraryService.listArchivedParts,
      queryParams,
    );

    yield put(archivedPartsList.success(response));
  } catch (error) {
    yield put(archivedPartsList.error(error));
  }
}

function* watchOpenPart(action) {
  const { partMasterUuid, partMasterVersion } = action.payload;
  //hack that prevents two card form being added to the stack at once, which would cause an error when
  //back is clicked
  const nextLocation = yield select(getNextLocation);
  if (nextLocation.pathname.includes(partViewerRoutePrefix)) {
    history.replace(
      `${partViewerRoutePrefix}/${partMasterUuid}?version=${partMasterVersion}`,
    );
  } else {
    history.push(
      `${partViewerRoutePrefix}/${partMasterUuid}?version=${partMasterVersion}`,
    );
  }
}

function* watchAddSelectedPart(action) {
  const { part } = action.payload;
  const selectedParts = yield select(getSelectedPartsMap);
  selectedParts[part.uuid] = part;
  yield put(partLibraryPage.setSelectedParts({ selectedParts }));
}

function* watchRemoveSelectedPart(action) {
  const { part } = action.payload;
  const selectedParts = yield select(getSelectedPartsMap);
  delete selectedParts[part.uuid];
  yield put(partLibraryPage.setSelectedParts({ selectedParts }));
  if (Object.entries(selectedParts).length == 0)
    yield put(partLibraryPage.closeDrawer());
}

function* watchArchiveParts() {
  try {
    const groupId = yield select(getGroupId);
    const uuids = yield select(getSelectedPartsUuidArray);
    const parts = uuids.map((uuid) => ({ uuid, isArchived: true }));
    const currentPartCount = yield select(getTotalPartCount);
    const newPartCount = currentPartCount - uuids.length;
    const page = yield select(getPage);
    const maxPage = Math.ceil(newPartCount / PAGE_SIZE);
    yield call(partService.bulkUpdate, groupId, { parts });
    yield put(archiveParts.success({ numParts: uuids.length }));
    yield put(archivedPartsList.request());
    yield put(partLibraryPage.closeActionBar());
    maxPage < page
      ? yield put(partLibraryPage.paginate({ pageNumber: maxPage }))
      : yield put(partsList.request());
  } catch (error) {
    yield put(archiveParts.error(error));
  }
}

function* watchRestoreParts() {
  try {
    const groupId = yield select(getGroupId);
    const uuids = yield select(getSelectedPartsUuidArray);
    const parts = uuids.map((uuid) => ({ uuid, isArchived: false }));
    const currentPartCount = yield select(getArchivedPartsCount);
    const newPartCount = currentPartCount - uuids.length;
    const page = yield select(getPage);
    const maxPage = Math.ceil(newPartCount / PAGE_SIZE);
    yield call(partService.bulkUpdate, groupId, { parts });
    yield put(restoreParts.success({ numParts: uuids.length }));
    yield put(partsList.request());
    yield put(partLibraryPage.closeActionBar());
    maxPage < page
      ? yield put(partLibraryPage.paginate({ pageNumber: maxPage }))
      : yield put(archivedPartsList.request());
  } catch (error) {
    yield put(archiveParts.error(error));
  }
}

function* watchPurgeParts() {
  try {
    const groupId = yield select(getGroupId);
    const uuids = yield select(getSelectedPartsUuidArray);
    const currentPartCount = yield select(getArchivedPartsCount);
    const newPartCount = currentPartCount - uuids.length;
    const page = yield select(getPage);
    const maxPage = Math.ceil(newPartCount / PAGE_SIZE);
    yield call(partService.bulkPurge, groupId, { parts: uuids });
    yield put(purgeParts.success({ numParts: uuids.length }));
    yield put(partsList.request());
    yield put(partLibraryPage.closeActionBar());
    maxPage < page
      ? yield put(partLibraryPage.paginate({ pageNumber: maxPage }))
      : yield put(archivedPartsList.request());
  } catch (error) {
    yield put(purgeParts.error(error));
  }
}

const displaySuccessToast = (numParts, selectedQuote) => {
  const id = `open${Date.now()}`;
  const { number, revisionNumber } = selectedQuote;
  toast({
    status: 'success',
    title: `${numParts} Parts Added to Quote #${formatRevisedQuoteNumber(
      number,
      revisionNumber,
    )}`,
    description: (
      <Button
        as="a"
        variant="link"
        size="sm"
        href={`/quotes/edit/${number}${
          revisionNumber ? `-${revisionNumber}` : ''
        }`}
      >
        Go to quote
      </Button>
    ),
    duration: 10000,
    id,
  });
};

function* watchCopyPartsToQuote() {
  try {
    const creatingQuote = yield select(isCreatingQuote);
    if (creatingQuote) {
      const groupId = yield select(getGroupId);
      const response = yield call(quoteService.create, groupId);
      yield put(partLibraryPage.selectQuote({ quote: response }));
    }

    const uuids = yield select(getSelectedPartsUuidArray);
    const selectedQuote = yield select(getSelectedQuote);
    yield call(quoteService.copyPartsToQuote, selectedQuote.id, {
      partUuids: uuids,
    });
    yield put(
      copyPartsToQuote.success({
        numParts: uuids.length,
        quoteNumber: selectedQuote.number,
      }),
    );
    yield call(displaySuccessToast, uuids.length, selectedQuote);
  } catch (error) {
    yield put(copyPartsToQuote.error(error));
  }
}

function* watchOpenAddToQuoteDrawer() {
  yield put(draftedQuotes.request());
}

function* watchDraftedQuotesRequest() {
  try {
    const groupId = yield select(getGroupId);
    const response = yield call(quoteService.list, groupId, {
      status: 'draft',
    });
    yield put(
      draftedQuotes.success({
        draftedQuotes: response.results,
        hasMoreQuotes: Boolean(response.next),
      }),
    );
  } catch (error) {
    yield put(draftedQuotes.error(error));
  }
}

function* watchGetAdditionalQuotes() {
  try {
    const groupID = yield select(getGroupId);
    const page = yield select(getQuotesPage);
    const response = yield call(quoteService.list, groupID, {
      status: 'draft',
      page: page + 1,
    });
    yield put(
      getAdditionalQuotes.success({
        quotes: response.results,
        page: page + 1,
        hasMoreQuotes: Boolean(response.next),
      }),
    );
  } catch (error) {
    yield put(getAdditionalQuotes.error(error));
  }
}

function* watchMergePartsAsSupportingFiles(action) {
  const { parentUuid } = action.payload;
  const selectedUuids = yield select(getSelectedPartsUuidArray);
  const childPartUuids = selectedUuids.filter((uuid) => uuid !== parentUuid);

  try {
    const currentPartCount = yield select(getTotalPartCount);
    const newPartCount = currentPartCount - childPartUuids.length;
    const page = yield select(getPage);
    const maxPage = Math.ceil(newPartCount / PAGE_SIZE);
    yield call(partService.mergeAsSupportingFiles, parentUuid, childPartUuids);
    yield put(mergeParts.success({ numParts: childPartUuids.length }));
    yield put(partLibraryPage.closeActionBar());
    if (maxPage < page) {
      yield put(partLibraryPage.paginate({ pageNumber: maxPage }));
    } else {
      yield put(partsList.request());
    }
  } catch (error) {
    yield put(mergeParts.error(error));
  }
}

export function* initPartLibraryPage() {
  yield all([put(partLibraryPage.init())]);
}
